import React, { useEffect, useRef, useCallback, useMemo } from 'react';

import { useUserSettings } from '../../stores/settingsRegistry';
import { useToastStore } from '../../stores/ToastStore';
import { useChatManagementStore, initialExportState } from '../../stores/chatManagementStore';
import { useChatListStore } from '../../stores/chatListStore';
import { useChatHistoryStore } from '../../stores/chatHistoryStore';
import { useChatUIStore } from '../../stores/chatUIStore';

import {
	ExportFormat,
	ExportOptions,
	ExportTheme,
	ExportConfig,
} from '../../types/models';
import { IExportService } from '../../types/exportTypes';
import { apiClient } from '../../clients/APIClient';

import { TopBar } from './CMTopBar';
import { ChatListItem } from './CMList';
import { RightPanel, EmptyChatList } from './CMPanels';

import { buildExportOptions } from '../../utils/chatExportUtils';

import { ModalPortal } from '../common/ModalPortal';
import { ConfirmDeleteModal } from '../common/ConfirmDeleteModal';

import { useExportFlows } from '../../hooks/useExportFlows';

// Export Service is injected via props to allow platform-specific implementation.
interface ChatManagementModalProps {
	isOpen: boolean;
	onClose: () => void;
	exportService: IExportService;
	selectedChatId?: string;
}

export function ChatManagementModal({
	isOpen,
	onClose,
	exportService,
	selectedChatId,
}: ChatManagementModalProps) {
	const chats = useChatListStore((s) => s.chats);
	const selectChat = useChatListStore((s) => s.selectChat);
	const removeChat = useChatListStore((s) => s.removeChat);
	const stopGeneration = useChatListStore((s) => s.stopGeneration);
	const onNewChat = useChatListStore((s) => s.onNewChat);
	const renameChatDirect = useChatListStore((s) => s.renameChat);
	const chatId = useChatHistoryStore((s) => s.chatId);
	const isGenerating = useChatHistoryStore((s) => s.isGenerating);
	const selectedModel = useChatHistoryStore((s) => s.selectedModel);
	const setTargetChatId = useChatUIStore((s) => s.setTargetChatId);
	const setTargetChatTitle = useChatUIStore((s) => s.setTargetChatTitle);
	const setRenameOpen = useChatUIStore((s) => s.setRenameOpen);
	const { hideToast, showToast, setModalOpen } = useToastStore();

	const { actions } = useChatManagementStore();
	const exportState = useChatManagementStore((s) => s.export);
	const selectionState = useChatManagementStore((s) => s.selection);
	const previewState = useChatManagementStore((s) => s.preview);
	const uiState = useChatManagementStore((s) => s.ui);

	const {
		generatePreview,
		handleExportSelected,
		handleExportSingle,
		handleCancelExport,
	} = useExportFlows(exportService);

	const userSettings = useUserSettings();
	const formatSetting = userSettings.helpers.getConfig(
		'export_options.format',
	);
	const themeSetting = userSettings.helpers.getConfig('export_options.theme');
	const configSetting = userSettings.helpers.getConfig(
		'export_options.config',
	);

	const modalRef = useRef<HTMLDivElement>(null);
	const initializedRef = useRef(false);

	// Reset store state on unmount (modal is transient)
	useEffect(() => {
		return () => {
			actions.reset();
		};
	}, [actions]);

	// Track modal state for toast positioning
	useEffect(() => {
		setModalOpen(isOpen);
	}, [isOpen, setModalOpen]);

	// Handle escape key
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				if (exportState.mode) {
					actions.setExportMode(null);
					actions.setSelectedChats([]);
				} else {
					hideToast();
					onClose();
				}
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [onClose, exportState.mode, actions, hideToast]);

	// Sync export mode with selection count:
	// - Multiple selected → auto-enter bulk export panel
	// - None selected while in bulk → reset to details view
	// - Single mode is explicitly entered by user action, not selection-driven
	useEffect(() => {
		if (!isOpen) return;
		if (exportState.isExporting) return;
		if (selectionState.selectedChats.length > 1) {
			if (exportState.mode !== 'bulk') {
				actions.setExportMode('bulk');
			}
		} else if (selectionState.selectedChats.length === 0) {
			if (exportState.mode === 'bulk') {
				actions.setExportMode(null);
			}
		}
	}, [
		isOpen,
		exportState.isExporting,
		selectionState.selectedChats.length,
		exportState.mode,
		actions,
	]);

	// Generate preview when export mode or options change
	useEffect(() => {
		if (
			(exportState.mode === 'single' || exportState.mode === 'bulk') &&
			selectionState.selectedChat
		) {
			generatePreview(selectionState.selectedChat);
		}
	}, [
		generatePreview,
		exportState.mode,
		selectionState.selectedChat,
		exportState.format,
		exportState.theme,
		exportState.config,
	]);

	// ============= Handlers =============

	const loadChatDetails = useCallback(
		async (targetId: string) => {
			actions.setIsLoadingMessages(true);
			actions.setChatMessages(null);
			actions.setChatImages(null);

			try {
				const messages =
					await apiClient.history.getHistoryById(targetId);
				const messageCount = Array.isArray(messages)
					? messages.length
					: 0;
				const imageCount = Array.isArray(messages)
					? messages.reduce(
							(total, msg) => total + (msg.images?.length || 0),
							0,
						)
					: 0;
				actions.setChatMessages(messageCount);
				actions.setChatImages(imageCount);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error fetching chat messages:', error);
				actions.setChatMessages(0);
				actions.setChatImages(0);
				showToast('Failed to load chat details', 'error', {
					timeout: 3000,
					placement: 'bottom-center',
					adaptToModal: true,
				});
			} finally {
				actions.setIsLoadingMessages(false);
			}
		},
		[actions, showToast],
	);

	const handleSelectAll = useCallback(() => {
		const hasSelection = selectionState.selectedChats.length > 0;
		if (hasSelection) {
			actions.setSelectedChats([]);
			actions.setExportMode(null);
			const validChatId =
				chatId && chats.some((c) => c.id === chatId) ? chatId : null;
			if (validChatId) {
				actions.setSelectedChat(validChatId);
				loadChatDetails(validChatId);
			} else {
				actions.setSelectedChat(null);
			}
		} else {
			actions.setSelectedChats(chats.map((chat) => chat.id));
		}
	}, [
		chats,
		chatId,
		selectionState.selectedChats.length,
		loadChatDetails,
		actions,
	]);

	const handleChatToggle = useCallback(
		(targetChatId: string) => {
			actions.toggleChatSelection(targetChatId);
		},
		[actions],
	);

	const handleChatSelect = useCallback(
		async (targetChatId: string) => {
			if (targetChatId === selectionState.selectedChat) return;
			actions.setSelectedChat(targetChatId);
			await loadChatDetails(targetChatId);
		},
		[selectionState.selectedChat, loadChatDetails, actions],
	);

	const handleDeleteSelected = useCallback(() => {
		if (selectionState.selectedChats.length === 0) return;
		actions.setBulkDeleteOpen(true);
	}, [selectionState.selectedChats.length, actions]);

	const handleBulkDelete = useCallback(() => {
		const selected = selectionState.selectedChats;
		if (selected.length === 0) {
			actions.setBulkDeleteOpen(false);
			return;
		}

		selected.forEach((targetChatId) => removeChat(targetChatId));
		actions.setSelectedChats([]);

		if (
			selectionState.selectedChat &&
			selected.includes(selectionState.selectedChat)
		) {
			const remainingChats = chats.filter(
				(chat) => !selected.includes(chat.id),
			);
			actions.setSelectedChat(
				remainingChats.length > 0 ? remainingChats[0].id : null,
			);
		}

		actions.setBulkDeleteOpen(false);
	}, [selectionState, chats, removeChat, actions]);

	const handleAddToExport = useCallback(
		(targetChatId: string) => {
			if (!selectionState.selectedChats.includes(targetChatId)) {
				actions.setSelectedChats([
					...selectionState.selectedChats,
					targetChatId,
				]);
			}
		},
		[selectionState.selectedChats, actions],
	);

	const handleRemoveFromExport = useCallback(
		(targetChatId: string) => {
			actions.setSelectedChats(
				selectionState.selectedChats.filter(
					(id: string) => id !== targetChatId,
				),
			);
		},
		[selectionState.selectedChats, actions],
	);

	const handleExportOnly = useCallback(
		(targetChatId: string) => {
			actions.setSelectedChat(targetChatId);
			actions.setSelectedChats([]);
			actions.setExportMode('single');
		},
		[actions],
	);

	const handleOptionsChange = useCallback(
		(opts: ExportOptions) => {
			actions.setExportFormat(opts.format);
			actions.setExportTheme(opts.theme);
			actions.setExportConfig(opts.config);
		},
		[actions],
	);

	// Opens the rename modal (rename button in ChatListItem)
	const handleOpenRenameModal = useCallback(
		(targetChatId: string, title: string) => {
			setTargetChatId(targetChatId);
			setTargetChatTitle(title);
			setRenameOpen(true);
		},
		[setTargetChatId, setTargetChatTitle, setRenameOpen],
	);

	// Commits an inline title edit (double-click in ChatListItem)
	const handleRenameInline = useCallback(
		async (targetChatId: string, newTitle: string) => {
			await renameChatDirect(targetChatId, newTitle);
		},
		[renameChatDirect],
	);

	// Reset and initialize state when modal opens
	useEffect(() => {
		if (!isOpen) {
			initializedRef.current = false;
			return;
		}
		if (initializedRef.current) return;

		actions.resetExportState();
		actions.resetSelectionState();
		actions.resetPreviewState();

		if (formatSetting && themeSetting && configSetting) {
			const format = userSettings.helpers.getValue(
				formatSetting.key,
			) as ExportFormat;
			const theme = userSettings.helpers.getValue(
				themeSetting.key,
			) as ExportTheme;
			// NOTE: settings store returns `unknown`; double-cast required until
			// getValue is generically typed (getValue<T>(key): T).
			const config = userSettings.helpers.getValue(
				configSetting.key,
			) as unknown as ExportConfig;

			actions.initializeExportState({
				format: format || initialExportState.format,
				theme: theme || initialExportState.theme,
				config: config || initialExportState.config,
			});
		}

		const targetId =
			(selectedChatId &&
				chats.some((c) => c.id === selectedChatId) &&
				selectedChatId) ||
			(chatId &&
				chats.some((c) => c.id === chatId) &&
				chatId);

		if (targetId) {
			actions.setSelectedChat(targetId);
			actions.setSelectedChats([]);
			loadChatDetails(targetId);
		}

		initializedRef.current = true;
	}, [
		isOpen,
		chats,
		selectedChatId,
		chatId,
		formatSetting,
		themeSetting,
		configSetting,
		userSettings,
		actions,
		loadChatDetails,
	]);

	// ============= Memoized Values =============

	const selectedChatData = useMemo(
		() =>
			chats.find((chat) => chat.id === selectionState.selectedChat) ||
			null,
		[chats, selectionState.selectedChat],
	);

	const options = useMemo(
		() =>
			buildExportOptions(
				exportState.format,
				exportState.theme,
				selectedModel || 'Unknown',
				exportState.mode === 'single' && selectionState.selectedChat
					? selectionState.selectedChat
					: undefined,
				exportState.config,
				selectedChatData?.title,
			),
		[
			exportState.format,
			exportState.theme,
			exportState.mode,
			exportState.config,
			selectionState.selectedChat,
			selectedChatData?.title,
			selectedModel,
		],
	);

	if (!isOpen) return null;

	return (
		<ModalPortal isOpen={isOpen}>
			<div
				className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1000]"
				data-testid="chat-management-modal"
			>
				<div
					className="bg-neutral-700 p-md pb-xs pr-0 rounded-medium w-[90%] max-w-[1400px] h-[85%] max-h-[800px] shadow-large border-2 border-neutral-400 flex flex-col box-border"
					ref={modalRef}
					role="presentation"
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}
				>
					{/* Header */}
					<div className="flex justify-between items-center pb-xxs pr-lg mb-xxs">
						<h2 className="m-0 text-lg text-neutral-100">
							Manage Chats
						</h2>
						<button
							onClick={onClose}
							className="bg-transparent border-0 text-xl cursor-pointer text-neutral-100 hover:text-red-400 transition-colors duration-200"
							aria-label="Close chat management"
							title="Close chat management"
							type="button"
						>
							<i className="fas fa-times" />
						</button>
					</div>

					{/* Body */}
					<div className="flex-grow mb-xs text-neutral-100 flex flex-col justify-start h-full overflow-y-auto pr-lg">
						<TopBar
							selectedCount={selectionState.selectedChats.length}
							totalCount={chats.length}
							options={options}
							isExporting={exportState.isExporting}
							exportMode={exportState.mode}
							onSelectAll={handleSelectAll}
							onOptionsChange={handleOptionsChange}
							onExport={() => {
								if (exportState.mode === null) {
									if (
										selectionState.selectedChats.length ===
										1
									) {
										actions.setSelectedChat(
											selectionState.selectedChats[0],
										);
										actions.setExportMode('single');
									} else {
										actions.setExportMode('bulk');
									}
								} else {
									switch (exportState.mode) {
										case 'single':
											if (selectionState.selectedChat) {
												handleExportSingle(
													selectionState.selectedChat,
												);
											}
											break;
										case 'bulk':
											handleExportSelected();
											break;
										default:
											break;
									}
								}
							}}
							onDelete={handleDeleteSelected}
						/>

						{/* Dual Pane Layout */}
						<div className="flex-1 flex gap-6 overflow-hidden">
							{/* Left Panel - Chat List */}
							<div className="w-1/2 flex flex-col overflow-hidden">
								<div className="flex items-center justify-between mb-sm mr-4">
									<h3 className="m-0 text-sm font-medium text-neutral-100 uppercase tracking-wide">
										Chat History
									</h3>
									<button
										data-testid="new-chat-btn"
										className="w-6 h-6 flex bg-neutral-500 border-0 cursor-pointer rounded-pill px-1 transition-colors duration-200 hover:bg-red-600"
										style={
											{
												WebkitAppRegion: 'no-drag',
											} as React.CSSProperties
										}
										title="New chat"
										onClick={async () => {
											await apiClient.audio
												.stopTTSStream()
												.catch(() => {});
											if (isGenerating) {
												await stopGeneration();
											}
											onNewChat();
											onClose();
										}}
										aria-label="Start a new chat"
										type="button"
									>
										<i className="fas fa-pen-to-square text-neutral-100 text-sm self-center mx-auto" />
									</button>
								</div>
								<div className="flex-1 overflow-y-auto bg-neutral-600 border border-neutral-400 rounded-medium">
									{chats.length === 0 ? (
										<EmptyChatList noChats />
									) : (
										<div className="p-sm space-y-xs">
											{chats.map((chat) => (
												<ChatListItem
													key={chat.id}
													chat={chat}
													isSelected={selectionState.selectedChats.includes(
														chat.id,
													)}
													isActive={
														selectionState.selectedChat ===
														chat.id
													}
													isCurrentChat={
														chatId === chat.id
													}
													onSelect={() =>
														handleChatSelect(
															chat.id,
														)
													}
													onToggle={() =>
														handleChatToggle(
															chat.id,
														)
													}
													onOpen={() => {
														selectChat(chat.id);
														onClose();
													}}
													onExport={() => {
														handleExportSingle(
															chat.id,
														);
													}}
													onDelete={() => {
														actions.setSingleDelete(
															true,
															chat.id,
															chat.title,
														);
													}}
													onRename={
														handleRenameInline
													}
													onOpenRenameModal={
														handleOpenRenameModal
													}
												/>
											))}
										</div>
									)}
								</div>
							</div>

							{/* Right Panel */}
							<RightPanel
								mode={exportState.mode}
								options={options}
								exportState={exportState}
								selectedChatData={selectedChatData}
								selectedChatCount={
									selectionState.selectedChats.length
								}
								selectedChatMessages={
									selectionState.selectedChatMessages
								}
								selectedChatImages={
									selectionState.selectedChatImages
								}
								isLoadingMessages={
									selectionState.isLoadingMessages
								}
								previewState={previewState}
								chatId={chatId}
								selectedChats={selectionState.selectedChats}
								onExportSelected={handleExportSelected}
								onCancelExport={handleCancelExport}
								onExportSingle={handleExportSingle}
								onTriggerExport={(targetChatId: string) => {
									actions.setSelectedChat(targetChatId);
									actions.setSelectedChats([targetChatId]);
									actions.setExportMode('single');
								}}
								onOpenChat={(targetChatId: string) => {
									selectChat(targetChatId);
									onClose();
								}}
								onOptionsChange={handleOptionsChange}
								onAddToExport={handleAddToExport}
								onRemoveFromExport={handleRemoveFromExport}
								onExportOnly={handleExportOnly}
							/>
						</div>
					</div>
				</div>

				{/* Confirm Delete - Single */}
				<ConfirmDeleteModal
					isOpen={uiState.singleDelete.open}
					itemLabel="Chat"
					itemTitle={
						chats.find((c) => c.id === uiState.singleDelete.chatId)
							?.title
					}
					onCancel={() => actions.setSingleDelete(false)}
					onConfirm={() => {
						if (!uiState.singleDelete.chatId) return;
						removeChat(uiState.singleDelete.chatId);
						actions.setSelectedChats(
							selectionState.selectedChats.filter(
								(id: string) =>
									id !== uiState.singleDelete.chatId,
							),
						);
						if (
							selectionState.selectedChat ===
							uiState.singleDelete.chatId
						) {
							const remaining = chats.filter(
								(c) => c.id !== uiState.singleDelete.chatId,
							);
							actions.setSelectedChat(
								remaining.length > 0 ? remaining[0].id : null,
							);
						}
						actions.setSingleDelete(false);
					}}
				/>

				{/* Confirm Delete - Bulk */}
				<ConfirmDeleteModal
					isOpen={uiState.bulkDeleteOpen}
					itemLabel="Chat"
					count={selectionState.selectedChats.length}
					onCancel={() => actions.setBulkDeleteOpen(false)}
					onConfirm={handleBulkDelete}
				/>
			</div>
		</ModalPortal>
	);
}
